import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSEO } from "../lib/useSEO";
import { STATS, VALUE_PROPS, PLAINTIFF_STEPS, TESTIMONIALS, FAQS, SERVICED_STATES_SCHEMA, BUSINESS_CONTACT, GOOGLE_REVIEWS } from "../data/content";
import { TestimonialCard } from "../components/TestimonialCard";
import { ArrowRight, ShieldCheck, CheckCircle2, Scale, Users, Zap, Banknote } from "lucide-react";
import { GoogleReviewBadge, GoogleReviewCard } from "../components/GoogleReviewBadge";
import PreQualWidget from "../components/PreQualWidget";

const HERO_IMG = "https://images.unsplash.com/photo-1764258492945-96d083b455df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwY2FsbXxlbnwwfHx8fDE3NzgyNTQ0MTd8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  // Variant E (approved). GSC-optimized: stacks the two dominant head-term synonyms
  // ("pre-settlement funding" 68 imp + "lawsuit loans" 20 imp) that plaintiffs actually
  // search for, then combines the three highest-CTR triggers in this industry:
  // specific dollar range + specific speed + risk-reversal (non-recourse).
  // suffix disabled — the 63-char title is engineered end-to-end for the SERP.
  useSEO(
    "Pre-Settlement Funding & Lawsuit Loans in 24 Hours",
    "Attorney-founded pre-settlement funding for injury plaintiffs. $500–$25,000 against your active lawsuit, funded in 24 hours. Non-recourse — repay only if you win.",
    {
      canonical: "https://instabridgefunding.com/",
      suffix: false,
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "FinancialProduct",
            "name": "Instabridge Pre-Settlement Funding",
            "description": "Non-recourse cash advances of $500–$25,000 against an active personal-injury lawsuit. Funded within 24 hours of attorney verification. Repay only if the case is won.",
            "provider": {
              "@type": "Organization",
              "name": "Instabridge Funding",
              "url": "https://instabridgefunding.com",
            },
            "feesAndCommissionsSpecification": "Transparent tiered fee cap; no upfront fees; no credit check; no monthly payments.",
            "areaServed": SERVICED_STATES_SCHEMA,
          },
          {
            "@type": "Organization",
            "name": "Instabridge Funding",
            "url": "https://instabridgefunding.com",
            "logo": "https://instabridgefunding.com/logo.png",
            "sameAs": [],
            "telephone": "+1-216-245-5967",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": BUSINESS_CONTACT.street,
              "addressLocality": BUSINESS_CONTACT.city,
              "addressRegion": BUSINESS_CONTACT.region,
              "postalCode": BUSINESS_CONTACT.postalCode,
              "addressCountry": "US",
            },
            "description": "Attorney-founded pre-settlement funding company providing non-recourse cash advances to injury plaintiffs in select U.S. states.",
          },
        ],
      },
    },
  );

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A2540] text-white">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0A2540]/95 via-[#0A2540]/85 to-[#0A2540]/70" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            <div className="lg:col-span-7 max-w-3xl animate-fade-up">
              <div className="uppercase-label text-white/70 mb-6">Pre-settlement funding · Attorney-founded</div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight">
                Cash today.<br />
                <span className="font-semibold">Settled the right way.</span>
              </h1>
              <p className="mt-7 text-lg lg:text-xl text-white/75 max-w-2xl leading-relaxed">
                Attorney-founded pre-settlement funding and lawsuit funding for plaintiffs awaiting settlement. Non-recourse advances from $500 to $25,000 — 24-hour funding, no credit check, no monthly payments. Built by a practicing attorney, so we never overfund and never put your case at risk.
              </p>
              <div className="mt-7"><GoogleReviewBadge variant="dark" /></div>

              {/* Dual audience cards */}
              <div className="grid sm:grid-cols-2 gap-4 mt-10">
                <Link to="/apply" className="group bg-white/5 hover:bg-white/10 border border-white/15 rounded-2xl p-5 transition-colors" data-testid="hero-plaintiff-card">
                  <div className="flex items-center gap-2 uppercase-label !text-[#6BA8FF] mb-2"><Users className="w-3.5 h-3.5" /> For plaintiffs</div>
                  <div className="font-display text-xl font-semibold tracking-tight text-white">I need cash for my settlement</div>
                  <div className="mt-3 flex items-center gap-2 text-[#6BA8FF] text-sm font-semibold">
                    Apply now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
                <Link to="/for-attorneys" className="group bg-[#0066FF] hover:bg-[#0052CC] rounded-2xl p-5 transition-colors" data-testid="hero-attorney-card">
                  <div className="flex items-center gap-2 uppercase-label !text-white/85 mb-2"><Scale className="w-3.5 h-3.5" /> For attorneys</div>
                  <div className="font-display text-xl font-semibold tracking-tight text-white">Partner with Instabridge</div>
                  <div className="text-xs text-white/85 mt-1.5 leading-relaxed">Funding, attorney portal, payoff workflows, firm reporting — backed by attorney-first underwriting.</div>
                  <div className="mt-3 flex items-center gap-2 text-white text-sm font-semibold">
                    See how we help firms <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </div>

              {/* Subtle trust pill — funding speed + payout methods + encryption */}
              <div className="mt-6 inline-flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 rounded-full bg-white/5 border border-white/10" data-testid="hero-trust-pill">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-white/90 tracking-wide">
                  <ShieldCheck className="w-3 h-3 text-[#6BA8FF]" strokeWidth={2} /> Encrypted &amp; confidential
                </span>
                <span className="w-px h-3 bg-white/15" aria-hidden></span>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-white/90 tracking-wide">
                  <Zap className="w-3 h-3 text-[#6BA8FF]" strokeWidth={2} /> Funds in 24–48 hours
                </span>
                <span className="w-px h-3 bg-white/15" aria-hidden></span>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-white/90 tracking-wide">
                  <Banknote className="w-3 h-3 text-[#6BA8FF]" strokeWidth={2} /> ACH · Zelle · Cash App · Check
                </span>
              </div>
            </div>

            {/* 60-second pre-qual widget — kills the biggest mobile dropoff */}
            <div className="lg:col-span-5 lg:pt-2 animate-fade-up" style={{ animationDelay: "150ms" }}>
              <PreQualWidget />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-[#E5E7EB] py-6 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-[#6B7280] text-sm">
          {VALUE_PROPS.map((v) => (
            <div key={v.t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#0066FF]" /> {v.t}</div>
          ))}
        </div>
      </section>

      {/* STATS BAND */}
      <section className="py-20 lg:py-24 bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <div className="uppercase-label mb-4">The numbers</div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight text-[#0A2540] font-light leading-tight">
              Fast funding. No credit check. <span className="font-semibold">No repayment if you lose.</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E7EB] rounded-2xl overflow-hidden border border-[#E5E7EB]">
            {STATS.map((s) => (
              <div key={s.l} className="bg-white p-7 lg:p-8">
                <div className="font-display text-4xl lg:text-5xl font-semibold text-[#0A2540] tabular-nums">{s.v}</div>
                <div className="text-xs uppercase tracking-widest text-[#6B7280] mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="uppercase-label mb-5">The process</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight text-[#0A2540] font-light leading-tight">
                Three steps from <span className="font-semibold">accident to advance.</span>
              </h2>
              <p className="mt-5 text-[#6B7280] leading-relaxed max-w-md">
                We've reduced what used to take weeks into a single afternoon. Most clients are funded before their attorney finishes lunch.
              </p>
              <Link to="/how-it-works" className="inline-block mt-7">
                <Button variant="outline" className="rounded-full" data-testid="home-howit-btn">See full process →</Button>
              </Link>
            </div>
            <div className="lg:col-span-7 space-y-5">
              {PLAINTIFF_STEPS.map((s) => (
                <div key={s.n} className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-7 hover:bg-white hover:shadow-sm hover:-translate-y-0.5 transition-all">
                  <div className="flex items-start gap-6">
                    <div className="font-display text-3xl font-light text-[#0066FF] tabular-nums">{s.n}</div>
                    <div>
                      <h3 className="font-display text-xl font-semibold text-[#0A2540]">{s.t}</h3>
                      <p className="text-[#6B7280] mt-2 leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section className="py-24 lg:py-32 bg-[#0A2540] text-white relative overflow-hidden">
        <div className="absolute -right-40 -top-40 w-96 h-96 rounded-full bg-[#0066FF]/20 blur-3xl" />
        <div className="absolute -left-40 -bottom-40 w-96 h-96 rounded-full bg-[#0066FF]/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="uppercase-label !text-white/60 mb-5">The Instabridge difference</div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-light leading-tight">
              Built by a lawyer.<br /><span className="font-semibold">Run like it.</span>
            </h2>
            <p className="mt-7 text-white/80 leading-relaxed text-lg max-w-2xl">
              Adam Gurney is a practicing attorney. He didn't come from finance — he came from the same place you did: law school, courtrooms, and over a decade of cases that matter. He started Instabridge after seeing how broken the funding industry had become: companies built by finance people who saw attorneys as lead sources and plaintiffs as collateral.
            </p>
            <p className="mt-5 text-white/80 leading-relaxed text-lg max-w-2xl">
              We don't talk to attorneys like leads. We don't pressure plaintiffs. And we don't let advances grow past what the settlement can support. That's not marketing — it's underwriting policy, written by a lawyer who still tries cases.
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <Link to="/about"><Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-11 px-6">Meet Adam</Button></Link>
              <Link to="/for-attorneys"><Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-[#0A2540] rounded-full h-11 px-6">For attorneys →</Button></Link>
            </div>
          </div>
          <div className="lg:col-span-5 grid gap-4">
            <Pillar t="Settlement-first underwriting" d="We cap advances to protect the case. Your client gets what they need without eating the recovery." />
            <Pillar t="Attorney-founded" d="Not endorsed. Not adjacent. Founded and operated by a practicing lawyer." />
            <Pillar t="Zero friction for your paralegal" d="Partner with us. We handle intake, underwriting, and communication. Your paralegal will thank you." />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — verbatim Google reviews; no Review/AggregateRating schema (self-serving). */}
      <section className="py-24 lg:py-32 bg-[#F9FAFB]" data-testid="testimonials-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
            <div className="max-w-3xl">
              <div className="uppercase-label mb-5">What clients say</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tight text-[#0A2540] font-light leading-tight" data-testid="testimonials-heading">
                Real Stories From <span className="font-semibold">Clients We&apos;ve Helped</span>
              </h2>
              <p className="mt-4 text-sm text-[#6B7280]">Shared by clients on our Google Business Profile.</p>
            </div>
            <div className="shrink-0"><GoogleReviewCard /></div>
          </div>
          <div className="columns-1 md:columns-2 lg:columns-3 gap-5" data-testid="testimonials-grid">
            {TESTIMONIALS.map((t, i) => <TestimonialCard key={t.name} t={t} index={i} />)}
          </div>
          <div className="mt-8">
            <a href={GOOGLE_REVIEWS.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#0066FF] hover:underline underline-offset-4" data-testid="testimonials-all-reviews-link">
              Read all our reviews on Google ↗
            </a>
          </div>
        </div>
      </section>

      {/* WHY FIRMS PARTNER */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="uppercase-label mb-5">Why firms partner with Instabridge</div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tight text-[#0A2540] font-light leading-tight">
              Built for <span className="font-semibold">plaintiff firms.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5" data-testid="firm-value-props">
            <div className="border-l-2 border-[#0066FF] pl-6 py-2" data-testid="firm-value-attorney-founded">
              <Scale className="w-5 h-5 text-[#0066FF] mb-4" strokeWidth={1.75} />
              <h3 className="font-display text-xl font-semibold text-[#0A2540]">Attorney-Founded</h3>
              <p className="text-[#6B7280] leading-relaxed mt-3">
                Built by a practicing attorney who understands how funding decisions affect case outcomes.
              </p>
            </div>
            <div className="border-l-2 border-[#0066FF] pl-6 py-2" data-testid="firm-value-settlement-first">
              <ShieldCheck className="w-5 h-5 text-[#0066FF] mb-4" strokeWidth={1.75} />
              <h3 className="font-display text-xl font-semibold text-[#0A2540]">Settlement-First Underwriting</h3>
              <p className="text-[#6B7280] leading-relaxed mt-3">
                We focus on protecting the case and avoiding overfunding, not maximizing advances.
              </p>
            </div>
            <div className="border-l-2 border-[#0066FF] pl-6 py-2" data-testid="firm-value-technology">
              <Zap className="w-5 h-5 text-[#0066FF] mb-4" strokeWidth={1.75} />
              <h3 className="font-display text-xl font-semibold text-[#0A2540]">Technology That Saves Time</h3>
              <p className="text-[#6B7280] leading-relaxed mt-3">
                Attorney portal, digital verification, payoff requests, firm reporting, and streamlined workflows.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 lg:py-32 bg-[#F9FAFB]">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">FAQ</div>
          <h2 className="font-display text-4xl lg:text-5xl tracking-tight text-[#0A2540] font-light">Common questions</h2>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {FAQS.slice(0, 6).map((f, i) => (
              <AccordionItem key={f.q} value={`f${i}`} className="border-b border-[#E5E7EB]">
                <AccordionTrigger className="font-display text-lg text-[#0A2540] py-5 hover:no-underline" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#6B7280] leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8">
            <Link to="/faq" className="text-[#0066FF] font-medium hover:underline">See all questions →</Link>
          </div>
        </div>
      </section>

      {/* DUAL CTA */}
      <section className="py-20 bg-[#0A2540] text-white">
        <div className="mx-auto max-w-6xl px-6 lg:px-10 grid md:grid-cols-2 gap-6">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <div className="uppercase-label !text-white/60 mb-3">Plaintiffs</div>
            <h3 className="font-display text-2xl lg:text-3xl font-semibold">Need cash for your settlement?</h3>
            <p className="text-white/70 mt-3">Apply in under 5 minutes. Free, no obligation, no credit check.</p>
            <Link to="/apply" className="inline-block mt-6">
              <Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-11 px-6" data-testid="cta-bottom-apply">Apply now</Button>
            </Link>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
            <div className="uppercase-label !text-white/60 mb-3">Attorneys</div>
            <h3 className="font-display text-2xl lg:text-3xl font-semibold">Partner With Instabridge.</h3>
            <p className="text-white/70 mt-3">Settlement-first underwriting. Zero friction for your paralegal.</p>
            <Link to="/for-attorneys" className="inline-block mt-6">
              <Button className="bg-[#0066FF] text-white hover:bg-white hover:text-[#0A2540] rounded-full h-11 px-6" data-testid="cta-bottom-attorney">Learn more</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Pillar = ({ t, d }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-5 backdrop-blur-sm">
    <div className="flex items-start gap-3">
      <ShieldCheck className="w-5 h-5 text-[#0066FF] flex-shrink-0 mt-0.5" />
      <div>
        <div className="font-display font-semibold text-white">{t}</div>
        <div className="text-sm text-white/70 mt-1 leading-relaxed">{d}</div>
      </div>
    </div>
  </div>
);
